import React from 'react';
import { Map, Users, ArrowRightLeft, Target, UserCheck, Clock } from 'lucide-react';
import { Button } from '../components/Button';
const useCases = [{
  title: 'Territory-based routing',
  icon: Map,
  description: 'Route leads based on complex geographic rules. Map states, countries, or zip codes to specific regions, and assign them to the right territory pool automatically.',
  scenario: 'If State = CA, NY, TX → Route to Enterprise US Pool'
}, {
  title: 'Round robin lead assignment',
  icon: Users,
  description: 'Distribute leads evenly across a team. Use weighted round robin to ramp new reps, and automatically skip reps who are outside working hours or OOO.',
  scenario: 'Rotate evenly among 5 Mid-Market AEs (Skip if OOO)'
}, {
  title: 'SDR to AE handoffs',
  icon: ArrowRightLeft,
  description: 'Automate the handoff process when a meeting is booked. Route the account to the correct AE based on segment, and notify both the SDR and AE in Slack.',
  scenario: 'Meeting Booked → Find matching AE → Create Task & Alert'
}, {
  title: 'Named-account routing',
  icon: Target,
  description: 'Upload your target account lists. When an inbound lead matches a named account domain, bypass standard routing and send it directly to the assigned AE.',
  scenario: 'Domain matches Target List → Route to Target Account Owner'
}, {
  title: 'Existing owner routing',
  icon: UserCheck,
  description: 'Prevent channel conflict. Before applying any rules, check if the company already has an active owner or open deal, and route the new lead to them.',
  scenario: 'Check Company Owner → If exists, assign Lead to Company Owner'
}, {
  title: 'SLA enforcement & escalation',
  icon: Clock,
  description: "Ensure hot leads get worked fast. Start a timer on assignment. If the status doesn't change in 5 minutes, alert management or re-route to the next rep.",
  scenario: 'High Intent Lead → 5m Timer → If no action, Re-route & Alert Manager'
}];
export function UseCases() {
  return <div className="flex flex-col w-full bg-white" data-id="element-399">
      {/* Header */}
      <section className="pt-20 pb-16 bg-slate-50 border-b border-slate-200" data-id="element-400">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-401">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-6" data-id="element-402">
            Built for every routing scenario
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-403">
            From simple round robins to complex enterprise territory matrices,
            DemandPath handles the logic so you don't have to.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-20" data-id="element-404">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-405">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-406">
            {useCases.map((uc, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full" data-id="element-407">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 mb-6" data-id="element-408">
                  <uc.icon size={24} data-id="element-409" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-410">
                  {uc.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-411">
                  {uc.description}
                </p>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 mt-auto" data-id="element-412">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-413">
                    Example Logic
                  </div>
                  <div className="text-xs font-medium text-slate-800" data-id="element-414">
                    {uc.scenario}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-415">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-416">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6" data-id="element-417">
            Have a unique routing challenge?
          </h2>
          <p className="text-lg text-slate-300 mb-10" data-id="element-418">
            We've seen it all. Let's talk about how DemandPath can simplify your
            specific HubSpot setup.
          </p>
          <Button href="/book-a-demo" variant="primary" size="lg" className="bg-brand-500 hover:bg-brand-400 text-white border-none" data-id="element-419">
            Book a demo
          </Button>
        </div>
      </section>
    </div>;
}